/**
 * Theme JavaScript
 * Core functionality for the Shopify theme
 */

(function() {
  'use strict';

  // Utility functions
  var Utils = {
    /**
     * Debounce function calls
     */
    debounce: function(func, wait) {
      var timeout;
      return function() {
        var context = this;
        var args = arguments;
        clearTimeout(timeout);
        timeout = setTimeout(function() {
          func.apply(context, args);
        }, wait);
      };
    },

    /**
     * Format money based on Shopify settings
     */
    formatMoney: function(cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      var value = '';
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var formatString = format || '${{amount}}';

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = precision || 2;
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number == null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);
        var parts = number.split('.');
        var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        var cents = parts[1] ? decimal + parts[1] : '';

        return dollars + cents;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
      }

      return formatString.replace(placeholderRegex, value);
    },

    /**
     * Trap focus within an element
     */
    trapFocus: function(container, elementToFocus) {
      var focusableElements = container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      var firstFocusable = focusableElements[0];
      var lastFocusable = focusableElements[focusableElements.length - 1];

      function handleKeydown(e) {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }

      container.addEventListener('keydown', handleKeydown);

      if (elementToFocus) {
        elementToFocus.focus();
      } else if (firstFocusable) {
        firstFocusable.focus();
      }

      return {
        release: function() {
          container.removeEventListener('keydown', handleKeydown);
        }
      };
    }
  };

  // Mobile Menu
  var MobileMenu = {
    init: function() {
      this.menuToggle = document.querySelector('[data-mobile-menu-toggle]');
      this.menu = document.querySelector('[data-mobile-menu]');
      this.closeBtn = document.querySelector('[data-mobile-menu-close]');

      if (!this.menuToggle || !this.menu) return;

      this.bindEvents();
    },

    bindEvents: function() {
      var self = this;

      this.menuToggle.addEventListener('click', function() {
        self.open();
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', function() {
          self.close();
        });
      }

      // Close on escape
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && self.isOpen()) {
          self.close();
        }
      });

      // Close on overlay click
      this.menu.addEventListener('click', function(e) {
        if (e.target === self.menu) {
          self.close();
        }
      });
    },

    open: function() {
      this.menu.classList.add('mobile-menu--open');
      document.body.style.overflow = 'hidden';
      this.focusTrap = Utils.trapFocus(this.menu, this.closeBtn);
    },

    close: function() {
      this.menu.classList.remove('mobile-menu--open');
      document.body.style.overflow = '';
      if (this.focusTrap) {
        this.focusTrap.release();
      }
      this.menuToggle.focus();
    },

    isOpen: function() {
      return this.menu.classList.contains('mobile-menu--open');
    }
  };

  // Cart Drawer
  var CartDrawer = {
    init: function() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.triggers = document.querySelectorAll('[data-cart-trigger]');
      this.closeBtn = document.querySelector('[data-cart-drawer-close]');

      if (!this.drawer) return;

      this.bindEvents();
    },

    bindEvents: function() {
      var self = this;

      this.triggers.forEach(function(trigger) {
        trigger.addEventListener('click', function(e) {
          e.preventDefault();
          self.open();
        });
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', function() {
          self.close();
        });
      }

      // Close on escape
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && self.isOpen()) {
          self.close();
        }
      });

      // Close on overlay click
      this.drawer.addEventListener('click', function(e) {
        if (e.target === self.drawer) {
          self.close();
        }
      });
    },

    open: function() {
      this.drawer.classList.add('cart-drawer--open');
      document.body.style.overflow = 'hidden';
      this.focusTrap = Utils.trapFocus(this.drawer, this.closeBtn);
    },

    close: function() {
      this.drawer.classList.remove('cart-drawer--open');
      document.body.style.overflow = '';
      if (this.focusTrap) {
        this.focusTrap.release();
      }
    },

    isOpen: function() {
      return this.drawer.classList.contains('cart-drawer--open');
    },

    update: function(cart) {
      // Update cart count in header
      var cartCounts = document.querySelectorAll('[data-cart-count]');
      cartCounts.forEach(function(el) {
        el.textContent = cart.item_count;
        if (cart.item_count > 0) {
          el.classList.remove('hidden');
        } else {
          el.classList.add('hidden');
        }
      });

      // Update cart total
      var cartTotals = document.querySelectorAll('[data-cart-total]');
      cartTotals.forEach(function(el) {
        el.textContent = Utils.formatMoney(cart.total_price);
      });
    }
  };

  // Add to Cart
  var AddToCart = {
    init: function() {
      var forms = document.querySelectorAll('form[action="/cart/add"]');
      var self = this;

      forms.forEach(function(form) {
        form.addEventListener('submit', function(e) {
          e.preventDefault();
          self.submit(form);
        });
      });
    },

    submit: function(form) {
      var button = form.querySelector('[type="submit"]');
      var originalText = button.textContent;

      button.disabled = true;
      button.textContent = 'Adding...';

      var formData = new FormData(form);

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      })
      .then(function(response) {
        return response.json();
      })
      .then(function(data) {
        // Get updated cart
        return fetch('/cart.js');
      })
      .then(function(response) {
        return response.json();
      })
      .then(function(cart) {
        CartDrawer.update(cart);
        CartDrawer.open();
        button.textContent = 'Added!';
        setTimeout(function() {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);
      })
      .catch(function(error) {
        console.error('Error:', error);
        button.textContent = 'Error';
        setTimeout(function() {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);
      });
    }
  };

  // Header Scroll
  var HeaderScroll = {
    init: function() {
      this.header = document.querySelector('.header');
      if (!this.header) return;

      this.lastScroll = 0;
      this.bindEvents();
    },

    bindEvents: function() {
      var self = this;
      window.addEventListener('scroll', Utils.debounce(function() {
        self.onScroll();
      }, 10));
    },

    onScroll: function() {
      var currentScroll = window.pageYOffset;

      if (currentScroll > 100) {
        this.header.classList.add('header--scrolled');
      } else {
        this.header.classList.remove('header--scrolled');
      }

      this.lastScroll = currentScroll;
    }
  };

  // Accordion (for FAQ)
  var Accordion = {
    init: function() {
      var triggers = document.querySelectorAll('[data-accordion-trigger]');

      triggers.forEach(function(trigger) {
        trigger.addEventListener('click', function() {
          var item = this.closest('[data-accordion-item]');
          var content = item.querySelector('[data-accordion-content]');
          var isOpen = item.classList.contains('faq__item--open');

          // Close all items
          var allItems = document.querySelectorAll('[data-accordion-item]');
          allItems.forEach(function(i) {
            i.classList.remove('faq__item--open');
            i.querySelector('[data-accordion-trigger]').setAttribute('aria-expanded', 'false');
          });

          // Open clicked item if it was closed
          if (!isOpen) {
            item.classList.add('faq__item--open');
            this.setAttribute('aria-expanded', 'true');
          }
        });
      });
    }
  };

  // Smooth Scroll
  var SmoothScroll = {
    init: function() {
      var links = document.querySelectorAll('a[href^="#"]');

      links.forEach(function(link) {
        link.addEventListener('click', function(e) {
          var href = this.getAttribute('href');
          if (href === '#') return;

          var target = document.querySelector(href);
          if (!target) return;

          e.preventDefault();

          var headerHeight = document.querySelector('.header').offsetHeight || 0;
          var targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        });
      });
    }
  };

  // Lazy Loading Images
  var LazyLoad = {
    init: function() {
      if ('loading' in HTMLImageElement.prototype) {
        // Browser supports native lazy loading
        var images = document.querySelectorAll('img[loading="lazy"]');
        images.forEach(function(img) {
          if (img.dataset.src) {
            img.src = img.dataset.src;
          }
        });
      } else {
        // Fallback to IntersectionObserver
        var lazyImages = document.querySelectorAll('img[loading="lazy"]');

        if ('IntersectionObserver' in window) {
          var imageObserver = new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
              if (entry.isIntersecting) {
                var image = entry.target;
                if (image.dataset.src) {
                  image.src = image.dataset.src;
                }
                imageObserver.unobserve(image);
              }
            });
          });

          lazyImages.forEach(function(image) {
            imageObserver.observe(image);
          });
        }
      }
    }
  };

  // Announcement Bar Dismiss
  var AnnouncementBar = {
    init: function() {
      var dismissBtn = document.querySelector('[data-announcement-dismiss]');
      var bar = document.querySelector('.announcement-bar');

      if (!dismissBtn || !bar) return;

      dismissBtn.addEventListener('click', function() {
        bar.style.display = 'none';
        sessionStorage.setItem('announcementDismissed', 'true');
      });

      // Check if already dismissed
      if (sessionStorage.getItem('announcementDismissed') === 'true') {
        bar.style.display = 'none';
      }
    }
  };

  // Initialize all modules
  document.addEventListener('DOMContentLoaded', function() {
    MobileMenu.init();
    CartDrawer.init();
    AddToCart.init();
    HeaderScroll.init();
    Accordion.init();
    SmoothScroll.init();
    LazyLoad.init();
    AnnouncementBar.init();
  });

  // Expose utilities globally for use in sections
  window.theme = {
    Utils: Utils,
    CartDrawer: CartDrawer
  };

})();
